import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";
import axios from 'axios';
import rating from '../Images/rating.svg';

interface TestimonialData {
  name: string;
  content: string;
  id: number;
  image: string;
  position: string;
  review: number;
}

const Testimonial = () => {
  const api = process.env.REACT_APP_API_URL as string;
  const [testimonialData, setTestimonialData] = useState<TestimonialData[]>([]);
  const [activeSlider, setActiveSlider] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('right');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get<TestimonialData[]>(`${api}/auth/testimonial`);
        const transformed = response.data.map((item) => ({
          ...item,
          image: `${api}/${item.image.replace(/\\/g, '/')}`,
        }));
        setTestimonialData(transformed);
      } catch (error) {
        console.error("Testimonials not found:", error);
      }
    };
    fetchData();
  }, []);

  const handleNext = () => {
    setDirection('right');
    setActiveSlider((prev) =>
      prev + 2 >= testimonialData.length ? 0 : prev + 2
    );
  };

  const handlePrevious = () => {
    setDirection('left');
    setActiveSlider((prev) =>
      prev === 0 ? Math.max(testimonialData.length - 2, 0) : prev - 2
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setDirection('right');
      setActiveSlider((prev) =>
        prev + 1 >= testimonialData.length ? 0 : prev + 1
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonialData.length]);

  const variants = {
    enter: (dir: 'left' | 'right') => ({
      x: dir === 'right' ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
    exit: (dir: 'left' | 'right') => ({
      x: dir === 'right' ? -300 : 300,
      opacity: 0,
      transition: { duration: 0.5 },
    }),
  };

  return (
    <div className="relative min-h-[343px] w-[100%] overflow-hidden">
      <div className="!overflow-hidden">
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={activeSlider}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            custom={direction}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 absolute w-full"
          >
            {testimonialData.slice(activeSlider, activeSlider + 2).map((testimonial, index) => (
              <div
                key={index}
                className="flex flex-col space-y-4 bg-[#101729] border border-[#465468] rounded-2xl p-8 md:p-12 shadow-md"
              >
                {/* Top Section */}
                <div className="flex items-center space-x-4">
                  <div className="w-[80px] h-[80px] md:w-[100px] md:h-[100px] rounded-full overflow-hidden">
                    <img
                      className="w-full h-full object-cover"
                      src={testimonial.image}
                      alt={testimonial.name}
                    />
                  </div>
                  <div>
                    <h1 className="text-[18px] md:text-[20px] text-white">{testimonial.name}</h1>
                    <p className="text-[12px] text-[#CBD5E0]">{testimonial.position}</p>
                    <img src={rating} alt="rating" className="mt-1" />
                  </div>
                </div>

                {/* Content */}
                <p className="text-[14px] text-[#CBD5E0]">{testimonial.content}</p>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Left Arrow */}
      <div className="absolute left-1 top-1/2 transform -translate-y-1/2 z-40 bg-[#344056] w-[40px] h-[40px] flex justify-center items-center rounded-full">
        <button onClick={handlePrevious} className="text-white text-2xl">
          <RiArrowLeftSLine />
        </button>
      </div>

      {/* Right Arrow */}
      <div className="absolute right-1 top-1/2 transform -translate-y-1/2 z-40 bg-[#344056] w-[40px] h-[40px] flex justify-center items-center rounded-full">
        <button onClick={handleNext} className="text-white text-2xl">
          <RiArrowRightSLine />
        </button>
      </div>
    </div>
  );
};

export default Testimonial;
